// viewModalViewTypes.js


// Centralized configuration for view type cards.
// Add new view types here so ViewModal stays small.
// Timeline and Gantt both persist as type TIMELINE; optionKey distinguishes the preset.
export const buildViewOptions = (hasEnumFields) => ([
    { key: 'TABLE', label: 'Table', iconKey: 'table', note: 'Scan records in rows and columns.' },
    { key: 'CHART', label: 'Chart', iconKey: 'bar_chart', note: 'Visualize trends and totals with grouped metrics.' },
    {
        key: 'BOARD',
        label: 'Board',
        iconKey: 'view_kanban',
        note: hasEnumFields
            ? 'Group records into columns using a Select or single Relation field.'
            : 'Requires at least one Select or single Relation field to group records.',
        disabled: !hasEnumFields,
    },
    { key: 'GALLERY', label: 'Gallery', iconKey: 'gallery_thumbnail', note: 'Browse records as visual cards.' },
    { key: 'LIST', label: 'List', iconKey: 'format_list_bulleted', note: 'Read records in a compact stacked list.' },
    { key: 'ALPHA_LIST', label: 'Alphabetized List', iconKey: 'list_alt', note: 'Group records by first letter with an A–Z jump bar.' },
    { key: 'FEED', label: 'Feed', iconKey: 'dynamic_feed', note: 'Show updates in a chronological feed.' },
    { key: 'CALENDAR', label: 'Calendar', iconKey: 'calendar_month', note: 'Place records on a calendar by date.' },
    {
        key: 'TIMELINE',
        optionKey: 'TIMELINE:timeline',
        value: 'TIMELINE:timeline',
        preset: 'timeline',
        label: 'Timeline',
        iconKey: 'view_timeline',
        note: 'Lay records out across time spans.',
    },
    {
        key: 'TIMELINE',
        optionKey: 'TIMELINE:gantt',
        value: 'TIMELINE:gantt',
        preset: 'gantt',
        label: 'Gantt',
        iconKey: 'account_tree',
        note: 'Plan work with hierarchy and predecessors.',
    },
    { key: 'SCHEDULER', label: 'Scheduler', iconKey: 'calendar_view_day', note: 'Schedule one type of record against another across a single day.' },
    { key: 'MAP', label: 'Map', iconKey: 'map', note: 'Plot records with location data on a map.' },
    //{ key: 'FORM', label: 'Form', iconKey: 'dynamic_form' },
]);

/**
 * Resolves the select option key for a view type + timeline preset.
 *
 * @param {string} type
 * @param {string} [preset]
 * @returns {string}
 */
export function resolveViewTypeOptionKey(type, preset = 'timeline') {
    const normalizedType = String(type || '').trim().toUpperCase();
    if (normalizedType === 'TIMELINE') {
        return String(preset || '').trim().toLowerCase() === 'gantt'
            ? 'TIMELINE:gantt'
            : 'TIMELINE:timeline';
    }
    return normalizedType;
}

/**
 * Builds type + timeline preset defaults when a view-type card is selected.
 *
 * @param {object|string} optionOrKey - Option object or option key / optionKey.
 * @returns {{ type: string, preset: string, showHierarchy: boolean, showDependencies: boolean, allowScheduleEdit: boolean }}
 */
export function resolveViewTypeSelection(optionOrKey) {
    const option = optionOrKey && typeof optionOrKey === 'object'
        ? optionOrKey
        : null;
    const rawKey = option
        ? String(option.optionKey || option.value || option.key || '').trim()
        : String(optionOrKey || '').trim();
    const upperKey = rawKey.toUpperCase();

    if (upperKey === 'TIMELINE:GANTT' || (option?.key === 'TIMELINE' && option?.preset === 'gantt')) {
        return {
            type: 'TIMELINE',
            preset: 'gantt',
            showHierarchy: true,
            showDependencies: true,
            allowScheduleEdit: true,
        };
    }

    if (upperKey === 'TIMELINE:TIMELINE' || upperKey === 'TIMELINE' || option?.key === 'TIMELINE') {
        return {
            type: 'TIMELINE',
            preset: 'timeline',
            showHierarchy: false,
            showDependencies: false,
            allowScheduleEdit: false,
        };
    }

    return {
        type: upperKey || 'LIST',
        preset: 'timeline',
        showHierarchy: false,
        showDependencies: false,
        allowScheduleEdit: false,
    };
}
